<template>
  <div class="customer-category-tree-detail">
    <div class="detail-header">
      <div class="detail-header__left">
        <a class="detail-header__back" @click="state.goBack">
          ← 返回类目树列表
        </a>
        <h2 class="detail-header__title">
          编辑类目树：{{ state.form.treeName || '未命名类目树' }}
        </h2>
        <div class="detail-header__sub">
          {{
            state.contextText ||
              '请在当前上下文下编辑对客类目树结构与多语言名称'
          }}
        </div>
      </div>
      <div class="detail-header__actions">
        <a-button @click="state.addRootNode">新建一级类目</a-button>
      </div>
    </div>

    <div
      class="detail-content"
      :class="{ 'is-resizing': state.resizingTreePanel }"
      :style="{
        gridTemplateColumns: `${state.treePanelWidth}px 12px minmax(0, 1fr)`
      }"
    >
      <div class="tree-panel">
        <div class="tree-panel__toolbar">
          <a-input
            v-model:value="state.keyword"
            placeholder="搜索类目..."
            allow-clear
          />
          <a-button @click="state.toggleExpandAll">
            {{ state.allExpanded ? '收起全部' : '展开全部' }}
          </a-button>
        </div>
        <a-alert
          class="tree-panel__tip"
          type="info"
          show-icon
          :message="
            state.hasDraftNode
              ? '当前存在未保存草稿，保存或放弃后可继续拖拽排序。'
              : '拖到目标类目上半部插入其前面，下半部插入其后面；仅支持同级排序。'
          "
        />
        <div v-if="!state.form.treeData.length" class="tree-panel__empty">
          当前类目树暂无数据，请先新建一级类目。
        </div>
        <a-tree
          v-else
          :key="state.treeRenderKey"
          class="tree-panel__tree"
          :class="{ 'is-draft-mode': state.hasDraftNode }"
          :tree-data="state.filteredTreeData"
          :allow-drop="state.allowDrop"
          block-node
          :draggable="!state.hasDraftNode"
          :expanded-keys="state.expandedKeys"
          :selected-keys="state.selectedKey ? [state.selectedKey] : []"
          @expand="state.handleExpand"
          @dragstart="state.handleDragStart"
          @dragend="state.handleDragEnd"
          @drop="state.handleDrop"
          @select="state.handleSelect"
        >
          <template #title="node">
            <div
              class="tree-node"
              :class="{
                'is-selected': `${node.key}` === `${state.selectedKey}`,
                'is-dragging': `${node.key}` === `${state.draggingKey}`
              }"
            >
              <span class="tree-node__main">
                <holder-outlined class="tree-node__drag-handle" />
                <span class="tree-node__label">
                  {{
                    node.defaultName ||
                      (node.isNew ? '新建类目（草稿）' : node.title)
                  }}
                </span>
              </span>
              <div class="tree-node__actions" @click.stop>
                <a-switch
                  size="small"
                  :checked="node.status === 1"
                  checked-children="启用"
                  un-checked-children="禁用"
                  @change="checked => state.setNodeStatus(node.key, checked)"
                />
                <a-tooltip title="新增子类目">
                  <span
                    class="tree-node__action"
                    :class="{ 'is-disabled': !state.canAddChild(node.key) }"
                    @click.stop="state.quickAddChild(node.key)"
                  >
                    <plus-outlined />
                  </span>
                </a-tooltip>
                <a-popconfirm
                  title="确认删除当前节点吗？"
                  ok-text="确认"
                  cancel-text="取消"
                  @confirm="state.removeNodeByKey(node.key)"
                >
                  <a-tooltip title="删除类目">
                    <span
                      class="tree-node__action tree-node__action--delete"
                      @click.stop
                    >
                      <delete-outlined />
                    </span>
                  </a-tooltip>
                </a-popconfirm>
              </div>
            </div>
          </template>
        </a-tree>
      </div>

      <div
        class="panel-resizer"
        role="separator"
        aria-label="调整类目树宽度"
        aria-orientation="vertical"
        title="左右拖动调整宽度，双击恢复默认宽度"
        @mousedown="state.startTreePanelResize"
        @dblclick="state.resetTreePanelWidth"
      >
        <span class="panel-resizer__line" />
      </div>

      <div class="form-panel">
        <div v-if="!state.selectedNode" class="form-panel__empty">
          {{
            state.form.treeData.length
              ? '点击左侧类目查看详情并编辑多语言名称'
              : '当前类目树暂无类目，请先在左侧新建一级类目。'
          }}
        </div>
        <template v-else>
          <div class="form-panel__title">编辑类目</div>
          <a-form layout="vertical">
            <a-form-item label="默认名称（运营识别）" required>
              <a-input
                ref="defaultNameInputRef"
                v-model:value="state.selectedNode.defaultName"
                placeholder="后台展示用，前台会按站点语言配置展示"
                @change="state.syncSelectedTitle"
              />
            </a-form-item>
            <a-form-item label="父类目">
              <a-input :value="state.selectedParentName" disabled />
            </a-form-item>
            <a-form-item label="层级">
              <a-input :value="`${state.selectedLevel} 级类目`" disabled />
            </a-form-item>
            <a-form-item label="状态">
              <a-switch
                v-model:checked="state.selectedNodeStatusChecked"
                checked-children="启用"
                un-checked-children="禁用"
              />
            </a-form-item>
            <div class="form-panel__locale-title">
              <span class="form-panel__locale-required">*</span>多语言名称
            </div>
            <a-tabs
              v-model:activeKey="state.activeLanguageCode"
              class="form-panel__locale-tabs"
              size="small"
            >
              <a-tab-pane v-for="lang in state.langList" :key="lang.value">
                <template #tab>
                  {{ lang.label
                  }}{{
                    lang.value === state.defaultLanguageCode ? '（默认）' : ''
                  }}<span
                    v-if="!state.selectedNode.i18nNames[lang.value]?.trim()"
                    class="form-panel__locale-required"
                    >*</span
                  >
                </template>
                <a-input
                  v-model:value="state.selectedNode.i18nNames[lang.value]"
                  :placeholder="`请输入 ${lang.label} 名称`"
                  @update:value="
                    value => state.changeLanguageName(lang.value, value)
                  "
                />
              </a-tab-pane>
            </a-tabs>
            <div class="form-panel__locale-tip">
              当前站点按配置语言展示，所有语言名称均为必填。
            </div>
          </a-form>

          <div class="form-panel__actions">
            <a-button @click="state.cancelNodeEdit">取消</a-button>
            <a-button
              type="primary"
              :loading="state.saving"
              @click="state.saveTree"
            >
              保存修改
            </a-button>
          </div>
        </template>
      </div>
    </div>

    <a-modal
      v-model:visible="state.unsavedModalVisible"
      :title="state.unsavedModalTitle"
      :closable="false"
      :keyboard="false"
      :mask-closable="false"
      :footer="null"
    >
      <div v-if="state.selectedNode?.isNew">
        当前类目尚未创建，请保存草稿或放弃草稿后再切换至其他类目。
      </div>
      <div v-else>
        请先保存当前类目的修改，或放弃修改后再切换至其他类目。
      </div>
      <div v-if="!state.selectedNode?.isNew" class="unsaved-modal__changes">
        <div
          v-for="item in state.unsavedChangeList"
          :key="item.key"
          class="unsaved-modal__change"
        >
          <span class="unsaved-modal__change-label">{{ item.label }}：</span>
          <span class="unsaved-modal__change-before">{{ item.before }}</span>
          <span class="unsaved-modal__change-arrow">→</span>
          <span class="unsaved-modal__change-after">{{ item.after }}</span>
        </div>
      </div>
      <div class="unsaved-modal__actions">
        <a-button
          :disabled="state.switchSaving"
          @click="state.discardAndSwitch"
        >
          放弃直接切换
        </a-button>
        <a-button
          type="primary"
          :loading="state.switchSaving"
          @click="state.saveAndSwitch"
        >
          保存草稿并切换
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  toRefs
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import {
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined
} from '@ant-design/icons-vue'
import useAPI from 'UseApi'
import { common_lang_get } from '@/server/modules/3426'
import {
  createCustomerCategoryNode,
  deleteCustomerCategoryNode,
  getCustomerCategoryTree,
  moveCustomerCategoryNode,
  updateCustomerCategoryNode,
  updateCustomerCategoryTree
} from '../CustomerCategoryTreeConfig/service'

const { common_querySiteInfo_get_ } = useAPI(null, [3426])

const clone = value => JSON.parse(JSON.stringify(value))

const visitTree = (nodes, callback, parent = null) => {
  for (let index = 0; index < (nodes || []).length; index++) {
    const node = nodes[index]
    const result = callback(node, index, nodes, parent)
    if (result === false) return false
    if (node.children && node.children.length) {
      const childResult = visitTree(node.children, callback, node)
      if (childResult === false) return false
    }
  }
  return true
}

const filterTree = (nodes, keyword) => {
  if (!keyword) return nodes
  return (nodes || [])
    .map(node => {
      const children = filterTree(node.children || [], keyword)
      const matched = [
        node.defaultName || '',
        node.nameEn || '',
        ...Object.values(node.i18nNames || {})
      ]
        .join('')
        .toLowerCase()
        .includes(keyword.toLowerCase())
      if (!matched && !children.length) return null
      return {
        ...node,
        title: node.defaultName || node.title,
        children
      }
    })
    .filter(Boolean)
}

const createNode = (name, langList) => {
  const i18nNames = (langList || []).reduce((result, lang) => {
    result[lang.value] = ''
    return result
  }, {})
  return {
    key: `node_${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 8)}`,
    title: name,
    defaultName: name,
    nameEn: '',
    status: 1,
    icon: '',
    i18nNames,
    children: []
  }
}

const getNodeMeta = (nodes, key, parentKey = '') => {
  for (let index = 0; index < (nodes || []).length; index++) {
    const node = nodes[index]
    if (`${node.key}` === `${key}`) {
      return {
        node,
        index,
        siblings: nodes,
        parentKey
      }
    }
    const childMeta = getNodeMeta(node.children || [], key, `${node.key}`)
    if (childMeta) return childMeta
  }
  return null
}

const getNodeLevel = (nodes, key, level = 1) => {
  for (let index = 0; index < (nodes || []).length; index++) {
    const node = nodes[index]
    if (`${node.key}` === `${key}`) {
      return level
    }
    const childLevel = getNodeLevel(node.children || [], key, level + 1)
    if (childLevel) return childLevel
  }
  return 0
}

const getDropPlacement = info => {
  const dropPosition =
    info.dropPosition -
    Number(`${info.node?.pos || info.dropNode?.pos || '0-0'}`.split('-').pop())
  if (info.dropToGap) return dropPosition > 0 ? 'after' : 'before'

  const target = info.event?.target
  const wrapper = target?.closest?.('.ant-tree-node-content-wrapper')
  const rect = wrapper?.getBoundingClientRect?.()
  if (!rect) return 'before'
  return info.event.clientY > rect.top + rect.height / 2 ? 'after' : 'before'
}

const getExpandableKeys = nodes => {
  const keys = []
  visitTree(nodes, node => {
    if (node.children?.length) keys.push(node.key)
  })
  return keys
}

const getEditableNode = node => {
  if (!node) return null
  return {
    defaultName: node.defaultName || '',
    title: node.title || '',
    icon: node.icon || '',
    status: Number(node.status) === 0 ? 0 : 1,
    i18nNames: clone(node.i18nNames || {})
  }
}

const buildSelectedNodeDraft = node => {
  if (!node) return null
  return {
    ...node,
    ...getEditableNode(node),
    i18nNames: clone(node.i18nNames || {})
  }
}

export default {
  name: 'CustomerCategoryTreeConfigDetail',
  components: {
    DeleteOutlined,
    HolderOutlined,
    PlusOutlined
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const defaultNameInputRef = ref(null)
    let stopTreePanelResize = () => {}
    const state = reactive({
      recordId: '',
      detailRequestVersion: 0,
      saving: false,
      sorting: false,
      draggingKey: '',
      treePanelWidth: 520,
      resizingTreePanel: false,
      treeRenderKey: 0,
      brands: [],
      channels: [],
      sites: [],
      terminals: [],
      langList: [],
      defaultLanguageCode: '',
      activeLanguageCode: '',
      languageContextKey: '',
      form: {
        treeName: '',
        description: '',
        treeData: []
      },
      contextText: '',
      keyword: '',
      expandedKeys: [],
      allExpanded: computed(() => {
        const expandableKeys = getExpandableKeys(state.form.treeData)
        return (
          expandableKeys.length > 0 &&
          expandableKeys.every(key => state.expandedKeys.includes(key))
        )
      }),
      hasDraftNode: computed(() => {
        let hasDraft = false
        visitTree(state.form.treeData, node => {
          if (node.isNew) {
            hasDraft = true
            return false
          }
        })
        return hasDraft
      }),
      selectedKey: '',
      selectedNode: null,
      selectedNodeSource: null,
      selectedNodeOriginal: null,
      unsavedModalVisible: false,
      pendingSelectedKey: '',
      switchSaving: false,
      selectedNodeDirty: computed(
        () =>
          JSON.stringify(getEditableNode(state.selectedNode)) !==
          JSON.stringify(state.selectedNodeOriginal)
      ),
      unsavedModalTitle: computed(() => {
        if (state.selectedNode?.isNew) {
          return '当前类目为草稿，尚未保存'
        }
        return `当前${state.selectedNode?.defaultName ||
          state.selectedNode?.title ||
          ''}类目有未保存的修改`
      }),
      unsavedChangeList: computed(() => {
        const original = state.selectedNodeOriginal
        const current = getEditableNode(state.selectedNode)
        if (!original || !current) return []
        const changes = []
        const appendChange = (key, label, before, after) => {
          if (`${before ?? ''}` === `${after ?? ''}`) return
          changes.push({
            key,
            label,
            before: before === '' || before === undefined ? '（空）' : before,
            after: after === '' || after === undefined ? '（空）' : after
          })
        }

        appendChange(
          'defaultName',
          '默认名称',
          original.defaultName,
          current.defaultName
        )
        appendChange('icon', '图标', original.icon, current.icon)
        appendChange(
          'status',
          '状态',
          original.status === 1 ? '启用' : '禁用',
          current.status === 1 ? '启用' : '禁用'
        )
        const languageCodes = new Set([
          ...Object.keys(original.i18nNames || {}),
          ...Object.keys(current.i18nNames || {})
        ])
        languageCodes.forEach(code => {
          const language = state.langList.find(item => item.value === code)
          appendChange(
            `language-${code}`,
            `${language?.label || code}语言`,
            original.i18nNames?.[code],
            current.i18nNames?.[code]
          )
        })
        return changes
      }),
      selectedNodeStatusChecked: computed({
        get: () => state.selectedNode?.status === 1,
        set: value => {
          if (!state.selectedNode) return
          state.selectedNode.status = value ? 1 : 0
        }
      }),
      selectedLevel: computed(() =>
        state.selectedKey
          ? getNodeLevel(state.form.treeData, state.selectedKey)
          : 0
      ),
      selectedParentName: computed(() => {
        if (!state.selectedKey) return '顶级类目'
        const currentMeta = getNodeMeta(state.form.treeData, state.selectedKey)
        if (!currentMeta?.parentKey) return '顶级类目'
        const parentMeta = getNodeMeta(
          state.form.treeData,
          currentMeta.parentKey
        )
        return parentMeta?.node?.defaultName || '顶级类目'
      }),
      filteredTreeData: computed(() =>
        filterTree(
          (state.form.treeData || []).map(node => ({
            ...node,
            title: node.defaultName || node.title
          })),
          state.keyword
        )
      ),
      loadLanguages: async context => {
        const contextKey = [
          context.brandId,
          context.channelId,
          context.siteId,
          context.terminalId
        ].join('_')
        if (state.languageContextKey === contextKey && state.langList.length) {
          return
        }
        const { success, data, retInfo } = await common_lang_get(context)
        if (!success) throw new Error(retInfo || '站点语言加载失败')
        const languageMap = data?.languageMap || {}
        const langList = Object.entries(languageMap).map(([label, value]) => ({
          label,
          value
        }))
        const defaultLanguageCode = data?.defaultLanguageCode || ''
        if (
          defaultLanguageCode &&
          !langList.some(item => item.value === defaultLanguageCode)
        ) {
          langList.unshift({
            label: defaultLanguageCode,
            value: defaultLanguageCode
          })
        }
        state.langList = langList
        state.defaultLanguageCode = defaultLanguageCode
        if (
          !state.activeLanguageCode ||
          !langList.some(item => item.value === state.activeLanguageCode)
        ) {
          state.activeLanguageCode =
            defaultLanguageCode || langList[0]?.value || ''
        }
        state.languageContextKey = contextKey
      },
      fillNodeLanguages: nodes => {
        visitTree(nodes, node => {
          node.i18nNames = { ...(node.i18nNames || {}) }
          state.langList.forEach(lang => {
            if (node.i18nNames[lang.value] === undefined) {
              node.i18nNames[lang.value] = ''
            }
          })
        })
      },
      loadDetail: async preferredKey => {
        const requestVersion = ++state.detailRequestVersion
        try {
          const record = await getCustomerCategoryTree(state.recordId)
          if (requestVersion !== state.detailRequestVersion) return
          if (!record) {
            message.error('未找到对应类目树')
            router.replace({ name: 'CustomerCategoryTreeConfig' })
            return
          }
          state.form = {
            treeName: record.treeName,
            description: record.description,
            treeData: clone(record.treeData || [])
          }
          state.treeRenderKey += 1
          state.expandedKeys = getExpandableKeys(state.form.treeData)
          const brandId = record.brandId ?? route.query.brandId
          const channelId = record.channelId ?? route.query.channelId
          const siteId = record.siteId ?? route.query.siteId
          const terminalId = record.terminalId ?? route.query.terminalId
          await state.loadLanguages({
            brandId,
            channelId,
            siteId,
            terminalId
          })
          if (requestVersion !== state.detailRequestVersion) return
          state.fillNodeLanguages(state.form.treeData)
          const brandName =
            record.brandName ||
            state.brands.find(item => `${item.id}` === `${brandId}`)?.name ||
            '-'
          const channelName =
            record.channelName ||
            state.channels.find(item => `${item.id}` === `${channelId}`)
              ?.name ||
            '-'
          const siteName =
            record.siteName ||
            state.sites.find(item => `${item.id}` === `${siteId}`)?.name ||
            '-'
          state.contextText = `品牌：${brandName} / 渠道：${channelName} / 站点：${siteName}`
          const targetKey = preferredKey || state.selectedKey
          const targetMeta = targetKey
            ? getNodeMeta(state.form.treeData, targetKey)
            : null
          state.selectedKey =
            targetMeta?.node?.key || state.form.treeData[0]?.key || ''
          state.syncSelectedNode()
          state.captureSelectedNode()
        } catch (error) {
          message.error(error.message || '类目树详情加载失败')
        }
      },
      syncSelectedNode: () => {
        let current = null
        visitTree(state.form.treeData, node => {
          if (`${node.key}` === `${state.selectedKey}`) {
            current = node
            return false
          }
        })
        state.selectedNodeSource = current
        state.selectedNode = buildSelectedNodeDraft(current)
      },
      captureSelectedNode: () => {
        state.selectedNodeOriginal = getEditableNode(state.selectedNode)
      },
      switchSelectedNode: key => {
        state.selectedKey = key
        state.syncSelectedNode()
        state.captureSelectedNode()
      },
      selectDraftNode: node => {
        state.selectedKey = node.key
        state.selectedNodeSource = node
        state.selectedNode = buildSelectedNodeDraft(node)
        state.selectedNodeOriginal = {
          ...getEditableNode(node),
          defaultName: '',
          title: ''
        }
      },
      focusDefaultNameInput: () => {
        nextTick(() => {
          defaultNameInputRef.value?.focus({ cursor: 'all' })
          defaultNameInputRef.value?.select()
        })
      },
      handleSelect: (keys, info) => {
        const clickedKey = info?.node?.eventKey ?? info?.node?.key
        if (clickedKey === undefined || clickedKey === null) return
        if (`${clickedKey}` === `${state.selectedKey}`) return
        if (state.selectedNode?.isNew || state.selectedNodeDirty) {
          state.pendingSelectedKey = clickedKey
          state.unsavedModalVisible = true
          return
        }
        state.switchSelectedNode(clickedKey)
      },
      discardAndSwitch: async () => {
        const targetKey = state.pendingSelectedKey
        if (state.selectedNode?.isNew) {
          state.unsavedModalVisible = false
          state.pendingSelectedKey = ''
          await state.loadDetail(targetKey)
          return
        }
        if (state.selectedNodeSource && state.selectedNodeOriginal) {
          Object.assign(
            state.selectedNodeSource,
            clone(state.selectedNodeOriginal)
          )
        }
        state.unsavedModalVisible = false
        state.pendingSelectedKey = ''
        state.switchSelectedNode(targetKey)
      },
      saveAndSwitch: async () => {
        if (!state.selectedNode) return
        state.switchSaving = true
        const targetKey = state.pendingSelectedKey
        try {
          if (!state.validateSelectedNode()) {
            throw new Error('')
          }
          if (state.selectedNode.isNew) {
            await createCustomerCategoryNode(
              state.recordId,
              state.selectedNode,
              state.selectedNode.parentId
            )
          } else {
            await updateCustomerCategoryNode(state.selectedNode)
          }
          state.unsavedModalVisible = false
          state.pendingSelectedKey = ''
          message.success('当前类目已保存')
          await state.loadDetail(targetKey)
        } catch (error) {
          state.unsavedModalVisible = false
          state.pendingSelectedKey = ''
          message.error(error.message || '当前类目保存失败，已取消切换')
        } finally {
          state.switchSaving = false
        }
      },
      handleExpand: keys => {
        state.expandedKeys = keys
      },
      toggleExpandAll: () => {
        state.expandedKeys = state.allExpanded
          ? []
          : getExpandableKeys(state.form.treeData)
      },
      startTreePanelResize: event => {
        event.preventDefault()
        stopTreePanelResize()
        const startX = event.clientX
        const startWidth = state.treePanelWidth
        const contentWidth = event.currentTarget.parentElement?.clientWidth || 0
        const maxWidth = Math.max(360, Math.floor(contentWidth * 0.7))

        const handleMouseMove = moveEvent => {
          const targetWidth = startWidth + moveEvent.clientX - startX
          state.treePanelWidth = Math.min(maxWidth, Math.max(360, targetWidth))
        }
        const handleMouseUp = () => {
          stopTreePanelResize()
        }
        stopTreePanelResize = () => {
          window.removeEventListener('mousemove', handleMouseMove)
          window.removeEventListener('mouseup', handleMouseUp)
          state.resizingTreePanel = false
        }
        state.resizingTreePanel = true
        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)
      },
      resetTreePanelWidth: () => {
        state.treePanelWidth = 520
      },
      allowDrop: info => {
        if (state.hasDraftNode) return false
        if (state.sorting) return false
        if (state.keyword) return false
        if (!info.dragNode || !info.dropNode) return false

        const dragKey = info.dragNode.eventKey || info.dragNode.key
        const dropKey = info.dropNode.eventKey || info.dropNode.key
        if (!dragKey || !dropKey || `${dragKey}` === `${dropKey}`) {
          return false
        }

        const dragMeta = getNodeMeta(state.form.treeData, dragKey)
        const dropMeta = getNodeMeta(state.form.treeData, dropKey)
        if (!dragMeta || !dropMeta) return false
        const dropPosition =
          info.dropPosition -
          Number(`${info.dropNode?.pos || '0-0'}`.split('-').pop())

        // vc-tree 会将第一个子节点上方的落点识别为父节点内部，
        // 保留这个落点，用于将同级节点移到首位。
        if (dropPosition === 0) {
          if (`${dragMeta.parentKey}` === `${dropKey}`) return true
        }

        return `${dragMeta.parentKey}` === `${dropMeta.parentKey}`
      },
      handleDragStart: info => {
        state.draggingKey =
          info.node?.eventKey || info.node?.key || info.dragNode?.key || ''
      },
      handleDragEnd: () => {
        state.draggingKey = ''
      },
      handleDrop: async info => {
        state.draggingKey = ''
        if (state.keyword) {
          message.warning('搜索结果中不支持拖拽排序，请先清空搜索条件')
          return
        }
        const dragKey = info.dragNode?.eventKey || info.dragNode?.key
        const dropKey = info.node?.eventKey || info.node?.key
        if (!dragKey || !dropKey || `${dragKey}` === `${dropKey}`) {
          return
        }

        const dragMeta = getNodeMeta(state.form.treeData, dragKey)
        const dropMeta = getNodeMeta(state.form.treeData, dropKey)
        if (!dragMeta || !dropMeta) return

        const dropOnNode = !info.dropToGap
        const moveToFirst =
          dropOnNode && `${dragMeta.parentKey}` === `${dropKey}`
        if (
          !moveToFirst &&
          `${dragMeta.parentKey}` !== `${dropMeta.parentKey}`
        ) {
          message.warning('仅支持同级拖拽排序')
          return
        }

        const siblings = dragMeta.siblings
        const [dragNode] = siblings.splice(dragMeta.index, 1)
        let insertIndex = 0

        if (!moveToFirst) {
          const targetIndex = siblings.findIndex(
            item => `${item.key}` === `${dropKey}`
          )
          const placement = getDropPlacement(info)
          insertIndex =
            targetIndex < 0 ? 0 : targetIndex + (placement === 'after' ? 1 : 0)
        }

        siblings.splice(insertIndex, 0, dragNode)
        state.sorting = true
        try {
          await moveCustomerCategoryNode(
            dragNode.nodeId || dragNode.id || dragNode.key,
            dragMeta.parentKey || undefined,
            siblings.map(item => item.nodeId || item.id || item.key)
          )
          message.success('排序已更新')
        } catch (error) {
          message.error(error.message || '排序更新失败')
          await state.loadDetail(state.selectedKey)
        } finally {
          state.sorting = false
        }
      },
      syncSelectedTitle: () => {
        if (!state.selectedNode) return
        state.selectedNode.title = state.selectedNode.defaultName
      },
      validateSelectedNode: () => {
        const node = state.selectedNode
        if (!node) return true
        if (!node.defaultName?.trim()) {
          message.error('默认名称不能为空')
          return false
        }
        const emptyLang = state.langList.find(
          lang => !`${node.i18nNames?.[lang.value] || ''}`.trim()
        )
        if (emptyLang) {
          state.activeLanguageCode = emptyLang.value
          message.error(
            `请填写 ${emptyLang.label} 语言名称，多语言名称均为必填`
          )
          return false
        }
        return true
      },
      changeLanguageName: (langCode, value) => {
        if (!state.selectedNode) return
        state.selectedNode.i18nNames[langCode] = value
      },
      setNodeStatus: (key, checked) => {
        const currentMeta = getNodeMeta(state.form.treeData, key)
        if (!currentMeta) return
        currentMeta.node.status = checked ? 1 : 0
        if (`${state.selectedKey}` === `${key}` && state.selectedNode) {
          state.selectedNode.status = checked ? 1 : 0
        }
      },
      addRootNode: async () => {
        if (state.hasDraftNode) {
          message.warning('请先保存或取消当前新建类目')
          return
        }
        if (state.selectedNodeDirty) {
          message.warning('请先保存或取消当前类目的修改')
          return
        }
        const node = createNode('', state.langList)
        node.isNew = true
        state.form.treeData.push(node)
        state.selectDraftNode(node)
        state.focusDefaultNameInput()
      },
      canAddChild: key => {
        const currentMeta = getNodeMeta(state.form.treeData, key)
        if (!currentMeta || currentMeta.node.isNew) return false
        const currentLevel = getNodeLevel(state.form.treeData, key)
        return currentLevel > 0 && currentLevel < 3
      },
      quickAddChild: async key => {
        if (state.hasDraftNode) {
          message.warning('请先保存或取消当前新建类目')
          return
        }
        if (state.selectedNodeDirty) {
          message.warning('请先保存或取消当前类目的修改')
          return
        }
        if (!state.canAddChild(key)) {
          return
        }
        const currentMeta = getNodeMeta(state.form.treeData, key)
        if (!currentMeta) return
        const node = createNode('', state.langList)
        node.isNew = true
        node.parentId = currentMeta.node.nodeId || currentMeta.node.id
        currentMeta.node.children = currentMeta.node.children || []
        currentMeta.node.children.push(node)
        if (!state.expandedKeys.includes(currentMeta.node.key)) {
          state.expandedKeys.push(currentMeta.node.key)
        }
        state.selectDraftNode(node)
        state.focusDefaultNameInput()
      },
      removeNodeByKey: async key => {
        if (!key) return
        const currentMeta = getNodeMeta(state.form.treeData, key)
        if (!currentMeta) return
        if (currentMeta.node.isNew) {
          await state.loadDetail(currentMeta.node.parentId)
          message.success('已取消新建类目')
          return
        }
        try {
          await deleteCustomerCategoryNode(
            currentMeta.node.nodeId || currentMeta.node.id || key
          )
          state.selectedKey = ''
          await state.loadDetail()
          message.success('类目节点删除成功')
        } catch (error) {
          message.error(error.message || '类目节点删除失败')
        }
      },
      saveTree: async () => {
        if (!state.form.treeName) {
          message.error('类目树名称不能为空')
          return
        }
        state.saving = true
        try {
          if (!state.validateSelectedNode()) return
          if (state.selectedNode?.isNew) {
            const nodeId = await createCustomerCategoryNode(
              state.recordId,
              state.selectedNode,
              state.selectedNode.parentId
            )
            message.success('类目创建成功')
            await state.loadDetail(nodeId)
          } else {
            await updateCustomerCategoryTree(state.recordId, {
              treeName: state.form.treeName,
              description: state.form.description
            })
            if (state.selectedNode) {
              await updateCustomerCategoryNode(state.selectedNode)
            }
            message.success('保存成功')
            await state.loadDetail(state.selectedKey)
          }
        } catch (error) {
          message.error(error.message || '保存失败')
        } finally {
          state.saving = false
        }
      },
      cancelNodeEdit: async () => {
        if (state.selectedNode?.isNew) {
          await state.loadDetail(state.selectedNode.parentId)
          return
        }
        const currentKey = state.selectedKey
        await state.loadDetail(currentKey)
        if (!currentKey) return
        const currentMeta = getNodeMeta(state.form.treeData, currentKey)
        if (currentMeta) {
          state.selectedKey = currentKey
          state.syncSelectedNode()
        }
      },
      goBack: () => {
        router.push({ name: 'CustomerCategoryTreeConfig' })
      }
    })

    onMounted(async () => {
      state.recordId = route.query.id
      await common_querySiteInfo_get_({ state })
      await state.loadDetail()
    })

    onBeforeUnmount(() => {
      stopTreePanelResize()
    })

    return {
      ...toRefs(state),
      state,
      defaultNameInputRef
    }
  }
}
</script>

<style lang="scss" scoped>
.customer-category-tree-detail {
  padding: 16px;

  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;

    &__back {
      display: inline-block;
      margin-bottom: 8px;
      color: #666;
    }

    &__title {
      margin: 0;
      font-size: 24px;
    }

    &__sub {
      color: #999;
      margin-top: 6px;
    }

    &__actions {
      display: flex;
      align-items: center;
    }
  }

  .detail-content {
    display: grid;
    align-items: stretch;

    &.is-resizing {
      cursor: col-resize;
      user-select: none;
    }
  }

  .panel-resizer {
    position: relative;
    cursor: col-resize;

    &__line {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 50%;
      width: 2px;
      background: #d9d9d9;
      border-radius: 2px;
      transform: translateX(-50%);
      transition: width 0.2s ease, background 0.2s ease;
    }

    &:hover &__line,
    .is-resizing &__line {
      width: 4px;
      background: #1677ff;
    }
  }

  .tree-panel,
  .form-panel {
    background: #ffffff;
    border-radius: 4px;
    padding: 16px;
    min-height: 640px;
  }

  .tree-panel {
    &__toolbar {
      display: flex;
      gap: 8px;
      margin-bottom: 12px;

      .ant-input-affix-wrapper {
        flex: 1;
      }
    }

    &__tip {
      margin-bottom: 12px;
    }

    &__empty {
      min-height: 560px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      color: #999;
      text-align: center;
      border: 1px dashed #d9d9d9;
      border-radius: 8px;
      background: #fafafa;
    }

    &__tree {
      max-height: 560px;
      overflow: auto;

      :deep(.ant-tree-node-content-wrapper.ant-tree-node-selected) {
        background: transparent;
      }

      :deep(.ant-tree-drop-indicator) {
        height: 4px;
        background: #1677ff;
        border-radius: 2px;
        box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.14);
      }

      :deep(.ant-tree-treenode) {
        min-height: 42px;
        padding: 2px 0;
      }

      :deep(.ant-tree-treenode-dragging .tree-node) {
        transform: scale(1.04);
        opacity: 0.48;
      }

      :deep(.ant-tree-node-content-wrapper) {
        display: flex;
        align-items: center;
        min-height: 38px;
        padding-top: 4px;
        padding-bottom: 4px;
      }

      :deep(.ant-tree-title) {
        width: 100%;
      }

      &.is-draft-mode .tree-node__drag-handle {
        color: #c0c4cc;
        cursor: not-allowed;
        opacity: 0.35;
      }
    }
  }

  .tree-node {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    transform-origin: center;
    transition: transform 0.16s ease, opacity 0.16s ease, box-shadow 0.16s ease;

    &.is-dragging {
      position: relative;
      z-index: 2;
      transform: scale(1.04);
      opacity: 0.48;
      background: #e6f4ff;
      box-shadow: 0 4px 14px rgba(22, 119, 255, 0.28);
      cursor: grabbing;
    }

    &.is-selected {
      color: #1677ff;
      background: #e6f7ff;
      border-radius: 2px;
    }

    &__main {
      display: flex;
      align-items: center;
      min-width: 0;
      flex: 1;
    }

    &__drag-handle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      flex: none;
      margin-right: 4px;
      color: #a0a7b4;
      cursor: grab;
      opacity: 0.75;
      border-radius: 4px;
      transition: color 0.2s ease, opacity 0.2s ease;
    }

    &:hover &__drag-handle {
      color: #1677ff;
      opacity: 1;
      background: #e6f4ff;
    }

    &__label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 12px;
      opacity: 1;
    }

    &__action {
      color: #1677ff;
      cursor: pointer;
      font-size: 15px;
      line-height: 1;

      &.is-disabled {
        color: #c0c4cc;
        cursor: not-allowed;
      }
    }

    &__action--delete {
      color: #ff4d4f;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  }

  .form-panel {
    &__title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 16px;
    }

    &__empty {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #999;
    }

    &__actions {
      margin-top: 24px;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    &__locale-title {
      margin-bottom: 8px;
      font-weight: 500;
      color: #333;
    }

    &__locale-required {
      color: #ff4d4f;
      margin-right: 4px;
    }

    &__locale-tabs {
      margin-bottom: 8px;
    }

    &__locale-tip {
      color: #999;
      font-size: 12px;
      margin-top: 8px;
    }
  }

  .mt16 {
    margin-top: 16px;
  }
}

.unsaved-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.unsaved-modal__changes {
  max-height: 240px;
  margin-top: 16px;
  padding: 12px;
  overflow: auto;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
}

.unsaved-modal__change {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr) 20px minmax(0, 1fr);
  gap: 8px;
  align-items: start;

  & + & {
    margin-top: 10px;
  }
}

.unsaved-modal__change-label {
  color: #666;
  text-align: right;
}

.unsaved-modal__change-before {
  color: #999;
  text-decoration: line-through;
  word-break: break-all;
}

.unsaved-modal__change-arrow {
  color: #999;
  text-align: center;
}

.unsaved-modal__change-after {
  color: #1677ff;
  font-weight: 500;
  word-break: break-all;
}
</style>
